// 하나 바이블 공과 데이터(public/data/hana-bible-lessons.json) 조회와 챗봇 질문 분기
// 원본: 총회 제111회기 홍보 브로슈어 기반 hanabible.md → scripts/build-hana-lessons.mjs
import axios from "axios";

// 사이트 공통 부서 값 (contentsHanaPage 의 groups 와 같은 값)
export const HANA_GROUPS = [
  { label: "영유아부", value: "INFANT" },
  { label: "유치부", value: "KINDERGARTEN" },
  { label: "유년부", value: "CHILD" },
  { label: "초등부", value: "ELEMENTARY" },
  { label: "청소년", value: "TEENAGER" },
  { label: "대학 및 청년부", value: "SENIOR" },
  { label: "장년부", value: "ADULT" },
];

export const groupLabelOf = (value) => HANA_GROUPS.find((g) => g.value === value)?.label ?? "";

// 챗봇 꾸러미에 보여 줄 자료 종류 (hana-bible/v1/page 의 division 값, 노출 순서)
export const BUNDLE_DIVISIONS = [
  { division: "공과시연", label: "공과시연영상" },
  { division: "성경 스토리", label: "성경스토리영상" },
  { division: "단원찬양", label: "단원송" },
  { division: "단원챈트", label: "단원 챈트" },
  { division: "오감 활동", label: "오감활동" },
  { division: "선행학습", label: "선행학습영상" },
  { division: "가정예배", label: "가정예배영상" },
  { division: "교사교육", label: "교사교육영상" },
];

let lessonsPromise = null;

// 공과별 검색 태그(public/data/hana-bible-tags.json)를 공과에 붙인다.
// 태그는 개발 단계에서 LLM으로 만들고 사람이 검토한 정적 데이터다. 실행 중에는 AI를 부르지 않는다.
export const attachTags = (lessons, tagMap) => lessons.map((l) => ({ ...l, tags: (tagMap && tagMap[l.lessonRef]) || [] }));

const fetchJson = (url) =>
  fetch(url).then((res) => {
    if (!res.ok) throw new Error(`${url} 를 불러오지 못했습니다 (${res.status})`);
    return res.json();
  });

// 공과 데이터는 처음 필요할 때 한 번만 불러온다 (태그가 없어도 공과 답변은 된다)
export const loadLessons = () => {
  if (!lessonsPromise) {
    lessonsPromise = Promise.all([fetchJson("/data/hana-bible-lessons.json"), fetchJson("/data/hana-bible-tags.json").catch(() => null)])
      .then(([data, tagData]) => attachTags(data.lessons, tagData?.tags))
      .catch((error) => {
        lessonsPromise = null; // 다음 질문에서 다시 시도
        throw error;
      });
  }
  return lessonsPromise;
};

export const semesterName = (l) => `${l.course}과정 ${l.semester}학기`;
export const lessonName = (l) => (l.lesson != null ? `${l.lesson}과` : "특별 공과");

export const findLesson = (lessons, semesterKey, lesson) =>
  lessons.find((l) => l.semesterKey === semesterKey && l.lesson === Number(lesson)) ?? null;

// 정규 공과 순서상 다음 과 (22과 → 같은 과정 2학기 23과, 6과정 2학기 44과 → 1과정 1학기 1과)
export const nextLesson = (lessons, current) => {
  const regular = lessons.filter((l) => !l.isSpecial);
  const i = regular.findIndex((l) => l.lessonRef === current.lessonRef);
  return i < 0 ? null : regular[(i + 1) % regular.length];
};

// 공과 하나의 자료를 종류별로 모은다. 부서가 없으면 전체 부서 자료.
// 종류마다 따로 조회하므로 실패한 종류는 빼고 나머지만 보여 준다.
export const fetchLessonBundle = async ({ group, semesterKey, lesson, perDivision = 3 }) => {
  const results = await Promise.allSettled(
    BUNDLE_DIVISIONS.map(({ division }) =>
      axios.get("/new_api/customer/item/hana-bible/v1/page", {
        params: { page: 1, size: perDivision, group: group || "", semester: semesterKey, lesson, division },
      }),
    ),
  );
  return BUNDLE_DIVISIONS.map((d, i) => {
    const r = results[i];
    const data = r.status === "fulfilled" ? r.value.data?.data : null;
    return { ...d, items: data?.itemList ?? [], total: data?.pageInfo?.total ?? 0 };
  }).filter((section) => section.items.length > 0);
};

// 절기 표현 → 공과 데이터의 occasionKey, 자료 제목에서 찾을 말
// (자료 제목 예: "성탄주일 유초등부 성경스토리영상 - 5과정2학기 하나바이블")
const OCCASIONS = [
  { re: /성탄|크리스마스/, key: "성탄주일", word: "성탄" },
  { re: /추수\s*감사|추수|감사\s*절/, key: "추수감사주일", word: "추수감사" },
  { re: /부활/, key: "부활주일", word: "부활" },
  { re: /종려|고난\s*주간/, key: "종려주일", word: "종려" },
  { re: /어린이\s*(주일|날)|청소년\s*주일/, key: "어린이주일", word: "어린이주일" },
  { re: /어버이/, key: "어버이주일", word: "어버이" },
  { re: /가족\s*사랑/, key: "가족사랑주일", word: "가족사랑" },
  { re: /나라\s*사랑|광복절|삼일절|3\.1절/, key: "나라사랑주일", word: "나라사랑" },
  { re: /전도\s*선교\s*주일|선교\s*주일|전도\s*주일/, key: "전도선교주일", word: "전도" },
  { re: /이단/, key: "이단경계주일", word: "이단" },
  { re: /이웃\s*사랑/, key: "이웃사랑주일", word: "이웃사랑" },
];

export const occasionWordOf = (key) => OCCASIONS.find((o) => o.key === key)?.word ?? null;

// 브로슈어: 2027년에 1과정 1학기부터 다시 시작 → 6년 주기로 올해 과정을 짐작한다 (고르기 순서에만 쓴다)
const courseOfYear = (year) => ((((year - 2027) % 6) + 6) % 6) + 1;

// 절기 특별 공과 후보. 저장된 학기가 있으면 그 학기를, 없으면 올해 과정부터 차례로.
export const findOccasionLessons = (lessons, key, preferSemesterKey) => {
  const found = lessons.filter((l) => l.isSpecial && l.occasionKey === key);
  const thisCourse = courseOfYear(new Date().getFullYear());
  const rank = (l) => (l.semesterKey === preferSemesterKey ? -1 : (l.course - thisCourse + 6) % 6);
  return found.sort((a, b) => rank(a) - rank(b) || a.semester - b.semester);
};

// 부서 → 자료 제목의 부서 표기 (통합 부서 표기 "영유아유치부", "유초등부" 포함)
const GROUP_TITLE_RE = {
  INFANT: /영유아/,
  KINDERGARTEN: /유치/,
  CHILD: /유년|유초등/,
  ELEMENTARY: /초등/,
  TEENAGER: /청소년/,
};

// 특별 공과 자료: 공과별 조회 API 에 특별 공과가 없어서, 절기 말과 "N과정 N학기"가 제목에 든 자료를 검색으로 모은다
export const fetchSpecialMaterials = async ({ lesson, group, limit = 6 }) => {
  const word = occasionWordOf(lesson.occasionKey);
  if (!word) return [];
  const res = await axios.get("/new_api/open/item/nl/v1/pagination", {
    params: { page: 1, size: 100, prop: "id", dir: "DESC", pageUnitSize: 100, keyword: word },
  });
  const semesterRe = new RegExp(`${lesson.course}\\s*과정\\s*${lesson.semester}\\s*학기`);
  const groupRe = GROUP_TITLE_RE[group];
  const items = (res.data?.data?.items ?? [])
    .filter((item) => (item.title || "").includes(word) && semesterRe.test(item.title))
    .map((item, i) => [groupRe && groupRe.test(item.title) ? 0 : 1, i, item])
    .sort((a, b) => a[0] - b[0] || a[1] - b[1])
    .map(([, , item]) => item);
  return items.length ? [{ division: "절기", label: "절기 자료", items: items.slice(0, limit), total: items.length }] : [];
};

// 자료 썸네일 주소 (상대 경로면 API 서버 기준)
export const thumbnailUrl = (item) => {
  const thumb =
    item?.thumb ||
    item?.thumbnailLink ||
    item?.thumbnailList?.find((t) => t.thumbnailType === "THUMBNAIL")?.thumbnailLink ||
    "";
  if (!thumb) return null;
  return /^https?:\/\//i.test(thumb) ? thumb : `https://eduwind-api.nodeblack.net/${thumb.replace(/^\//, "")}`;
};

export const SERVICE_INFO =
  "하나 바이블은 성경 속에서 하나님 나라를 배워 하나님 나라 백성으로 살아가도록 돕는 전 세대 통합 신앙교육과정이에요.\n" +
  "6년 12학기로, 1~3과정은 성경, 4과정은 교리, 5과정은 성품, 6과정은 세계관을 배웁니다. " +
  "영유아부부터 청소년부까지 같은 본문을 연령에 맞게 배워요.";
export const SERVICE_INFO_SOURCE = "총회 제111회기 홍보 브로슈어 10~11쪽";

// 처음 시작하는 교회를 위한 안내 (hanabible.md §1.5)
export const ADOPTION_INFO =
  "하나 바이블은 교단과 교회 규모, 부서 구성과 관계없이 사용할 수 있어요.\n\n" +
  "• 학생이 적거나 통합 부서라면: 모든 부서가 같은 교육과정과 성경 본문을 쓰기 때문에, 예배는 함께 드리고 성경공부는 연령별로 나눠 진행할 수 있어요.\n" +
  "• 교사 준비: 온라인 컨퍼런스, 교사교육 영상, 공과 시연 영상과 지도자용 자료로 준비할 수 있어요.\n" +
  "• 교재 고르기: 부서 → 과정·학기 → 교재 구성 확인 → 홀리원북 또는 기독교 서점에서 구매하시면 돼요.\n" +
  "• 시작 시기: 2027년에는 1과정 1학기(구약성경)부터 다시 시작해요.";
export const ADOPTION_INFO_SOURCE = "총회 제111회기 홍보 브로슈어 31쪽 · 실제 판매 경로와 일정은 사이트에서 확인해 주세요";
export const KAKAO_CHANNEL_URL = "https://pf.kakao.com/_YqHmu/chat";
// 「하나바이블 교육과정(1-6) 한눈에 보기」(2026-10-07판) 안내
export const EDU_CONTACT_PHONE = "02-559-5642~9";

// 챗봇이 답하지 않는 질문 (docs/chatbot-improvement-plan.md 9장 범위 밖). 엉뚱한 자료 대신 갈 곳을 안내한다.
// links: 사이트 안 경로(새 탭), external: 바깥 사이트
const OUT_OF_SCOPE = [
  // 교재 구매는 "주문"이 들어가도 주문·환불 안내보다 먼저
  {
    kind: "textbook",
    re: /교재\s*(는|를)?\s*(구매|구입|주문|사|어디)|책\s*(은|을)?\s*(어디서|사)|홀리원북/,
    text: "하나 바이블 교재는 홀리원북(www.holyonebook.com)이나 가까운 기독교 서점에서 구매하실 수 있어요. 이 사이트(총회교육몰)에서는 공과 수업 자료와 물품을 받으실 수 있어요.",
    external: { label: "홀리원북 열기", url: "https://www.holyonebook.com" },
  },
  {
    kind: "order",
    re: /환불|결제|주문|배송|취소|영수증|세금\s*계산서|입금|구매\s*내역/,
    text: `주문·결제·환불은 제가 처리하지 못해요.\n구매 내역은 마이페이지에서 보실 수 있고, 환불·취소는 카카오톡 상담이나 교육 문의 전화(${EDU_CONTACT_PHONE})로 도와드려요.`,
    links: [{ label: "마이페이지 구매 내역", path: "/mypage/list" }],
  },
  {
    kind: "account",
    re: /비밀번호|아이디\s*(를|가|찾|분실|잊)|로그인이\s*안|회원\s*탈퇴|탈퇴|휴대폰\s*인증/,
    text: "아이디·비밀번호는 '아이디/비밀번호 찾기'에서 휴대폰 번호로 찾으실 수 있어요. 그래도 안 되면 카카오톡으로 문의해 주세요.",
    links: [{ label: "아이디·비밀번호 찾기", path: "/help_find" }],
  },
  {
    kind: "download",
    re: /다운\s*(로드)?\s*(가|이)?\s*안|파일이\s*안\s*열|재생이\s*안|영상이\s*안\s*(나|보|열)/,
    text: "받으신 자료는 마이페이지 '내 콘텐츠함'에서 다시 받으실 수 있어요. 그래도 안 되면 자료 이름과 함께 카카오톡으로 알려 주세요.",
    links: [{ label: "내 콘텐츠함", path: "/mypage/contentsBox" }],
  },
  {
    kind: "bible",
    // 장·절만 있는 질문("요한복음 3장 16절 PPT")은 자료 검색이므로 해석을 묻는 말이 있을 때만
    re: /(무슨|어떤)\s*(뜻|의미)|뜻이\s*(뭐|무엇)|\d+\s*절\s*(의\s*)?(뜻|의미)|해석해|설교\s*(를|문)?\s*(써|작성|만들)/,
    text: "성경 해석이나 설교 작성에는 답하지 않아요. 브로슈어에 없는 내용을 지어낼 수 있어서예요.\n대신 하나 바이블 공과와 수업 자료는 바로 찾아 드릴 수 있어요.",
  },
];

// 챗봇이 도울 수 있는 일 예시 (답하지 못할 때 버튼으로 보여 준다)
export const HELP_EXAMPLES = ["이번 주 공과 보여 줘", "아이들이 자주 다퉈요", "성탄절 공과 알려 줘"];

// 고민·상황 표현 → 공과 주제어 (앞의 말일수록 가중치가 크다)
// 주제어는 공과명·학습요점에 실제로 있는 말로 고른다. 점검: npm run check:chatbot
const TOPIC_SYNONYMS = [
  // 관계·갈등
  [/다투|다퉈|다툼|싸우|싸워|싸움|갈등|미워/, ["용서", "화해", "공감"]],
  [/새\s*친구|새로\s*온|처음\s*온|전학|새신자/, ["환대", "수용", "존중"]],
  [/차별|편견|장애|다문화|외국인/, ["공평", "편견", "환대"]],
  [/불공평|억울|공정/, ["공평", "의로", "정직"]],
  [/이기적|자기만|양보|자기\s*중심/, ["희생", "개인주의", "섬기"]],
  [/협동|협력|단합|팀\s*활동|모둠/, ["협력", "하나 된", "공동체"]],
  [/욕을|욕설|험담|말버릇|말을\s*함부로|말이\s*거칠|뒷담/, ["말하기", "온유", "경청"]],
  [/칭찬|격려|기를\s*살/, ["격려", "인정", "칭찬"]],
  // 마음·성품
  [/거짓말|정직|속이/, ["정직", "진실"]],
  [/화가|화를|화내|분노|짜증|욱하/, ["절제", "온유", "오래 참"]],
  [/사춘기|반항/, ["온유", "정체성", "순종"]],
  [/순종|말을\s*안\s*들|말\s*안\s*들|말씀을\s*안\s*들|안\s*들어|안\s*듣|효도/, ["순종", "가정"]],
  [/욕심|질투|비교|부러워|시기/, ["자족", "감사"]],
  [/감사|고마/, ["감사"]],
  [/자존감|열등감|자신감|못났|못생|쓸모\s*없/, ["형상", "정체성", "사랑"]],
  [/용기|발표|나서지/, ["용기", "솔선수범"]],
  [/낙심|포기|좌절|실패|자꾸\s*틀/, ["소망", "긍정", "격려"]],
  [/슬픔|슬퍼|우울|상실|이별|돌아가셨|장례/, ["위로", "소망", "기쁨"]],
  [/게으|미루|성실|꾸준|대충/, ["성실", "충성", "일관"]],
  [/리더|임원|반장|회장|모범/, ["솔선수범", "본이 되는", "섬기"]],
  [/잘못했|죄책|회개|반성/, ["회개", "용서"]],
  // 신앙
  [/기도/, ["기도"]],
  [/예배|집중|산만|장난|떠들/, ["예배", "경청"]],
  [/성경\s*(을|안|읽)|말씀\s*(을\s*안\s*읽|안\s*읽|읽)|큐티|qt/i, ["말씀", "성경"]],
  [/믿음이\s*(약|없)|의심|하나님이\s*(정말|진짜)/, ["믿음", "견고한"]],
  [/구원|천국|하늘\s*나라|영생/, ["구원", "영원한", "소망"]],
  [/성령/, ["성령"]],
  [/교회\s*(가|란|는|생활)|교회에\s*(안|잘)|결석|출석/, ["교회", "공동체", "예배"]],
  [/전도|선교/, ["전도", "선교", "전파"]],
  [/창조|진화|과학/, ["창조", "자연주의"]],
  [/진리|다\s*맞|정답이\s*없/, ["진리", "상대주의"]],
  [/미신|운세|타로|별자리|부적|사주/, ["뉴에이지", "미신"]],
  // 가정·삶
  [/이혼|한부모|부모님이\s*싸/, ["가정", "회복", "위로"]],
  [/스마트폰|휴대폰|유튜브|미디어|sns|쇼츠/i, ["미디어", "분별"]],
  [/중독|게임/, ["중독", "절제", "미디어"]],
  [/ai|인공지능|챗gpt|로봇/i, ["인공지능", "기술"]],
  [/환경|기후|쓰레기|지구/, ["생태계", "청지기", "환경"]],
  [/돈|용돈|소비|쇼핑|물건/, ["소비주의", "자족", "청지기"]],
  [/진로|꿈|장래|직업/, ["일과 신앙", "직업", "배움"]],
  [/시험|공부|성적|학업|학원/, ["배움", "성실", "지혜"]],
  [/쉬지|쉼|휴식|바빠|지쳐|피곤/, ["일과 쉼", "안식"]],
  [/예술|그림|음악|노래/, ["예술", "아름다움"]],
  [/연애|이성|사귀|남친|여친|남자\s*친구|여자\s*친구/, ["이성 관계", "순결"]],
  [/성교육|음란|야한/, ["성적 순결", "순결"]],
  [/자해|자살|죽고\s*싶/, ["자살", "생명", "위로"]],
  [/낙태|생명|출산/, ["생명"]],
  [/노인|할머니|할아버지|늙/, ["나이 듦", "죽음", "존중"]],
  [/가난|어려운\s*이웃|봉사|섬김|나눔/, ["돌봄", "자비", "이웃"]],
  // 넓은 표현은 마지막에: 더 구체적인 규칙(질투·이성 친구·타로·게임 등)의 주제어가 앞서도록
  [/불안|걱정|두려|무서|겁이/, ["두려움", "용기", "인도"]],
  [/죽음|고난|힘들|아파|병/, ["고난", "회복", "생명"]],
  [/친구|따돌|왕따|외톨|소외/, ["존중", "공감", "돌봄"]],
  [/가족|부모님|형제|가정/, ["가정", "가족"]],
];

// 질문에 들어 있는 태그 점수 (긴 태그일수록 구체적이라 더 무겁게)
// 공과명에 그대로 들어 있는 태그는 그 공과의 핵심 주제라 더 무겁게 본다
const tagScore = (lesson, text) => {
  const t = text.toLowerCase();
  return (lesson.tags || []).reduce((sum, tag) => {
    if (!t.includes(tag.toLowerCase())) return sum;
    return sum + (tag.length >= 3 ? 2 : 1) + (lesson.title.includes(tag) ? 2 : 0);
  }, 0);
};

// 점수가 같으면 행동·고민 질문에 바로 쓰는 영역부터 (성품 → 세계관 → 교리 → 성경)
const AREA_ORDER = ["성품", "세계관", "교리", "성경"];
const areaRank = (l) => {
  const i = AREA_ORDER.indexOf(l.area);
  return i < 0 ? AREA_ORDER.length : i;
};

// 자료를 가리키는 말이 있으면 공과 추천이 아니라 자료 검색이다. 단 "공과"라고 말했으면 공과를 찾는 것
const MATERIAL_WORDS = /ppt|악보|영상|자료|이미지|템플릿|파일|음원|가사|율동|설교|찬양|활동지|워크시트|색칠|만들기|공작|교구|카드|포스터|현수막/i;
const wantsMaterial = (t) => MATERIAL_WORDS.test(t) && !/공과/.test(t);

// 규칙이 못 알아들은 질문을 공과 태그로 찾는다. 없으면 빈 배열 → 자료 검색
export const searchLessonsByTags = (lessons, question, limit = 3) => {
  const t = question.replace(/\s+/g, " ").trim();
  if (wantsMaterial(t)) return [];
  return lessons
    .filter((l) => !l.isSpecial)
    .map((l) => [tagScore(l, t), l])
    .filter(([score]) => score > 0)
    .sort((a, b) => b[0] - a[0] || areaRank(a[1]) - areaRank(b[1]))
    .slice(0, limit)
    .map(([, l]) => l);
};

// 주제어로 공과를 찾는다 (공과명 > 주제 인덱스 > 학습요점 > 단원명 순 가중치, 질문이 태그에 걸리면 더한다)
// primary: 앞에서부터 몇 개가 가장 먼저 걸린(가장 구체적인) 규칙의 주제어인지. 그 주제어를 더 무겁게 본다.
export const searchLessonsByTopic = (lessons, words, limit = 3, primary = 1, question = "") =>
  lessons
    .filter((l) => !l.isSpecial)
    .map((l) => {
      let score = 0;
      words.forEach((w, i) => {
        const weight = i === 0 ? 2 : i < primary ? 1.5 : 0.5; // 뒤에 걸린 넓은 규칙은 보조로만
        if (l.title.includes(w)) score += 5 * weight;
        if (l.topics.some((t) => t.includes(w))) score += 3 * weight;
        if (l.keyPoint.includes(w)) score += 2 * weight;
        if ((l.unitTitle || "").includes(w)) score += weight;
      });
      if (score > 0 && question) score += tagScore(l, question);
      return [score, l];
    })
    .filter(([score]) => score > 0)
    .sort((a, b) => b[0] - a[0])
    .slice(0, limit)
    .map(([, l]) => l);

/**
 * 질문을 챗봇이 직접 답할 수 있는 유형으로 분류한다. 해당하지 않으면 null → 기존 자료 검색(백엔드)으로 보낸다.
 * 확실한 표현만 잡는다: 애매하면 검색으로 넘기는 편이 엉뚱한 답보다 낫다.
 */
export const classifyQuestion = (text) => {
  const t = text.replace(/\s+/g, " ").trim();
  let m;

  if (/하나\s*바이블.*(뭐|무엇|소개|어떤|란)/.test(t)) return { type: "service" };

  // 범위 밖(주문·계정·다운로드·교재 구매·성경 해석)은 공과·검색보다 먼저 거른다
  const outOfScope = OUT_OF_SCOPE.find((o) => o.re.test(t) && !(o.kind === "bible" && /ppt|자료|악보|영상|공과|찬양/i.test(t)));
  if (outOfScope) return { type: "outOfScope", kind: outOfScope.kind, text: outOfScope.text, links: outOfScope.links || [], external: outOfScope.external || null };
  if (/이번\s*주|이번주|내일\s*(공과|수업)|주일\s*준비|수업\s*준비|공과\s*준비/.test(t)) return { type: "thisWeek" };

  // 절기 특별 공과: "성탄절 공과", "6과정 2학기 추수감사주일"
  const occasion = OCCASIONS.find((o) => o.re.test(t));
  if (occasion) {
    const sm = t.match(/([1-6])\s*과정\s*([12])\s*학기/);
    return { type: "occasion", key: occasion.key, semesterKey: sm ? `${sm[1]}-${sm[2]}` : null };
  }

  if ((m = t.match(/([1-6])\s*과정\s*([12])\s*학기\s*(\d{1,2})\s*과/))) {
    return { type: "lesson", semesterKey: `${m[1]}-${m[2]}`, lesson: Number(m[3]) };
  }
  // "1과정 1학기"처럼 과가 없으면 학기 안내
  if ((m = t.match(/([1-6])\s*과정\s*([12])\s*학기/))) {
    return { type: "semester", semesterKey: `${m[1]}-${m[2]}` };
  }
  // 과정 없이 "1과"만 말한 경우: 과정마다 다른 공과라 되묻는다
  if ((m = t.match(/(^|[^0-9])(\d{1,2})\s*과(?!정)/)) && Number(m[2]) >= 1 && Number(m[2]) <= 44) {
    return { type: "lessonNumber", lesson: Number(m[2]) };
  }

  // 도입 문의: "우리 교회도 쓸 수 있나요?", "통합 부서인데요" 등
  if (/쓸 수 있|사용할 수 있|도입|시작하려|시작하고 싶|처음 시작|어떻게 시작|통합\s*부서|학생이 적|작은 교회|소형 교회|교단/.test(t)) {
    return { type: "adoption" };
  }

  // "용서 색칠 공부 자료"처럼 자료를 찾는 말이 있으면 고민 질문이 아니라 자료 검색이다
  const matched = wantsMaterial(t) ? [] : TOPIC_SYNONYMS.filter(([re]) => re.test(t));
  if (matched.length) return { type: "topic", words: matched.flatMap(([, ws]) => ws), primary: matched[0][1].length };

  return null;
};
